/**
 * ComposerWizard — 5-step content creation flow (single-image + carousel).
 *
 *   Setup → (Narrative if carousel) → Prompts → Images → Caption
 *
 * Single posts still show the compact 2-column Image/Caption view at the end.
 * Carousels get a horizontal slide-strip with per-slide prompt edit, per-slide
 * regenerate, and a ZIP download of all rendered slides.
 */
import { useMemo, useState } from 'react';
import JSZip from 'jszip';
import {
  Calendar, Check, ChevronRight, Download, ImageIcon, Loader2, Package, RefreshCw, RotateCcw, Send, Sparkles, Wand2,
} from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { marketing } from '@/lib/supabase/marketing';
import { useContentBuckets } from '@/hooks/useContentBuckets';
import { useContentReferences, useCompositionAnchors, type ContentReference } from '@/hooks/useContentReferences';
import { useComposer, type ComposerStep, type SlideState, type AspectRatio } from '@/hooks/useComposer';
import { BestSlotChips } from './BestSlotChips';
import { useReferencePerformance, type RefPerf } from '@/hooks/useReferencePerformance';
import { driveImgSrc } from '@/lib/driveImg';

const ASPECT_OPTIONS: Array<{ id: AspectRatio; label: string; hint: string }> = [
  { id: '4:5', label: 'IG Portrait', hint: '4:5 · Meta\'s preferred feed shape' },
  { id: '1:1', label: 'Square', hint: '1:1 · safe for carousels' },
  { id: '3:4', label: 'Portrait', hint: '3:4 · taller than IG portrait' },
  { id: '9:16', label: 'Vertical', hint: '9:16 · Stories / Reels' },
  { id: '16:9', label: 'Landscape', hint: '16:9 · widescreen' },
];

const STEPS: Array<{ id: ComposerStep; label: string; carouselOnly?: boolean }> = [
  { id: 'setup',     label: 'Setup' },
  { id: 'narrative', label: 'Narrative', carouselOnly: true },
  { id: 'prompts',   label: 'Prompts' },
  { id: 'images',    label: 'Images' },
  { id: 'caption',   label: 'Caption' },
];

export function ComposerWizard() {
  const buckets = useContentBuckets();
  const c = useComposer();
  const { state } = c;

  const visibleSteps = STEPS.filter((s) => !s.carouselOnly || state.postKind === 'carousel');

  return (
    <div className="space-y-4">
      <StepBar current={state.step} steps={visibleSteps} />

      {state.error && (
        <div className="rounded-md border border-rose-500/40 bg-rose-500/5 px-3 py-2 text-sm text-rose-500">
          {state.error}
        </div>
      )}

      {state.step === 'setup' && (
        <SetupStep bucketsLoading={buckets.isLoading} buckets={buckets.data ?? []} composer={c} />
      )}
      {state.step === 'narrative' && <NarrativeStep composer={c} />}
      {state.step === 'prompts' && <PromptsStep composer={c} />}
      {(state.step === 'images' || state.step === 'caption' || state.step === 'done') && (
        <ImagesAndCaptionStep composer={c} />
      )}
    </div>
  );
}

function StepBar({ current, steps }: { current: ComposerStep; steps: Array<{ id: ComposerStep; label: string }> }) {
  const idx = steps.findIndex((s) => s.id === current);
  return (
    <ol className="flex flex-wrap items-center gap-1 text-xs">
      {steps.map((s, i) => {
        const active = current === s.id;
        const done = i < idx || current === 'done';
        return (
          <li key={s.id} className="flex items-center gap-1">
            <span
              className={cn(
                'flex h-5 w-5 items-center justify-center rounded-full border text-2xs tabular-nums',
                done ? 'border-emerald-500 bg-emerald-500 text-white'
                : active ? 'border-violet-500 bg-violet-500 text-white'
                : 'border-border/50 text-muted-foreground',
              )}
            >
              {i + 1}
            </span>
            <span className={cn(active ? 'font-medium text-foreground' : 'text-muted-foreground')}>
              {s.label}
            </span>
            {i < steps.length - 1 && <ChevronRight className="h-3 w-3 text-muted-foreground/50" />}
          </li>
        );
      })}
    </ol>
  );
}

function SetupStep({
  bucketsLoading, buckets, composer,
}: {
  bucketsLoading: boolean;
  buckets: ReturnType<typeof useContentBuckets>['data'] extends infer T ? (T extends undefined ? never : T) : never;
  composer: ReturnType<typeof useComposer>;
}) {
  const { state, configure, startNext } = composer;
  const activeBucket = buckets.find((b) => b.slug === state.bucketSlug);
  const subOptions = useMemo(
    () => (activeBucket?.sub_category_counts ?? []).filter((s) => s.sub_category).map((s) => s.sub_category as string),
    [activeBucket],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Step 1 · Setup</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <div className="mb-1.5 text-sm font-medium">Bucket</div>
          <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-4">
            {bucketsLoading && <div className="text-xs text-muted-foreground">loading…</div>}
            {buckets.map((b) => (
              <button
                key={b.slug}
                type="button"
                onClick={() => configure({ bucketSlug: b.slug, subCategory: null, pickedReferenceIds: [] })}
                className={cn(
                  'flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                  state.bucketSlug === b.slug ? 'border-violet-500 bg-violet-500/5' : 'border-border/50 hover:border-border',
                )}
              >
                <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: b.color_accent ?? '#888' }} />
                <div className="min-w-0">
                  <div className="font-medium leading-tight">{b.name}</div>
                  <div className="text-2xs text-muted-foreground">{b.ref_count} refs</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="mb-1.5 text-sm font-medium">Post kind</div>
          <div className="flex gap-2">
            <KindChip label="Single post" active={state.postKind === 'single'} onClick={() => configure({ postKind: 'single', slideCount: 1 })} />
            <KindChip label="Carousel" active={state.postKind === 'carousel'} onClick={() => configure({ postKind: 'carousel', slideCount: Math.max(3, state.slideCount) })} />
          </div>
          {state.postKind === 'carousel' && (
            <div className="mt-2 flex items-center gap-2">
              <label className="text-xs text-muted-foreground">Slide count</label>
              <input
                type="number"
                min={2}
                max={10}
                value={state.slideCount}
                onChange={(e) => configure({ slideCount: Number(e.target.value) })}
                className="w-16 rounded-md border border-border/50 bg-background px-2 py-1 text-sm tabular-nums"
              />
              <span className="text-2xs text-muted-foreground">(2–10)</span>
            </div>
          )}
        </div>

        <div>
          <div className="mb-1.5 text-sm font-medium">
            Aspect ratio
            {state.postKind === 'carousel' && (
              <span className="ml-1.5 text-2xs text-muted-foreground">
                (every slide renders at this shape — Meta crops mismatched slides)
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {ASPECT_OPTIONS.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => configure({ aspectRatio: a.id })}
                className={cn(
                  'rounded-md border px-2.5 py-1.5 text-left text-2xs transition-colors',
                  state.aspectRatio === a.id ? 'border-violet-500 bg-violet-500/5' : 'border-border/50 text-muted-foreground hover:text-foreground',
                )}
              >
                <div className="font-medium">{a.label}</div>
                <div className="text-muted-foreground/70">{a.hint}</div>
              </button>
            ))}
          </div>
        </div>

        {subOptions.length > 0 && (
          <div>
            <div className="mb-1.5 text-sm font-medium">
              Sub-category <span className="text-2xs text-muted-foreground">(scopes references)</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <SubChip label="(any in bucket)" active={state.subCategory === null} onClick={() => configure({ subCategory: null, pickedReferenceIds: [] })} />
              {subOptions.map((s) => (
                <SubChip key={s} label={s} active={state.subCategory === s} onClick={() => configure({ subCategory: s, pickedReferenceIds: [] })} />
              ))}
            </div>
          </div>
        )}

        {state.bucketSlug && (
          <ReferencePicker
            bucketSlug={state.bucketSlug}
            subCategory={state.subCategory}
            picked={state.pickedReferenceIds}
            onChange={(ids) => configure({ pickedReferenceIds: ids })}
          />
        )}

        {state.bucketSlug && (
          <CompositionAnchorPicker
            bucketSlug={state.bucketSlug}
            subCategory={state.subCategory}
            pickedRefIds={state.pickedReferenceIds}
            anchorId={state.compositionAnchorRefId}
            carryVessel={state.anchorCarryVessel}
            onChange={(id) => configure({ compositionAnchorRefId: id })}
            onCarryVesselChange={(v) => configure({ anchorCarryVessel: v })}
          />
        )}

        <div>
          <label className="mb-1.5 block text-sm font-medium">
            {state.postKind === 'single' ? 'Subject' : 'Subject (hero)'}{' '}
            <span className="text-2xs text-muted-foreground">
              {state.postKind === 'single' ? '(what the photo shows)' : '(Claude plans the rest of the slides from this)'}
            </span>
          </label>
          <textarea
            rows={3}
            value={state.subjectHint}
            onChange={(e) => configure({ subjectHint: e.target.value })}
            placeholder={state.postKind === 'single'
              ? 'e.g. Shawarma Chicken Bowl — roasted chicken, tahini, pickled onions, sumac, cucumber'
              : 'e.g. Falafel Bowl — crispy chickpea falafel, hummus, pickled beet, cucumber, herbs'}
            className="w-full rounded-md border border-border/50 bg-background px-3 py-2 text-sm placeholder:text-muted-foreground/60 focus:border-violet-500 focus:outline-none"
          />
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={startNext} disabled={!state.bucketSlug || !state.subjectHint.trim() || state.busy}>
            {state.busy ? (
              <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> {state.postKind === 'carousel' ? 'Claude is planning…' : 'Claude is drafting…'}</>
            ) : (
              <><Wand2 className="mr-1.5 h-3.5 w-3.5" /> {state.postKind === 'carousel' ? 'Plan carousel' : 'Draft prompt'}</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function NarrativeStep({ composer }: { composer: ReturnType<typeof useComposer> }) {
  const { state, updateSlide, configure, draftAllPrompts } = composer;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-base">
          <span>Step 2 · Narrative</span>
          <span className="text-2xs font-normal text-muted-foreground">{state.slides.length}-slide carousel</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Every slide shows the SAME dish, same plate, same lighting — only the camera changes. Slide 1 (hero) sets the composition using your references; slides 2..N re-shoot it with different framing directives.
        </p>
        <div className="space-y-2">
          {state.slides.map((sl) => (
            <div key={sl.index} className="rounded-md border border-border/50 p-3">
              <div className="mb-1.5 flex items-center gap-2 text-xs font-medium">
                <span className={cn(
                  'rounded px-1.5 py-0.5 text-2xs tabular-nums',
                  sl.index === 0 ? 'bg-violet-500/10 text-violet-500' : 'bg-muted/40 text-muted-foreground',
                )}>
                  slide {sl.index + 1} {sl.index === 0 && '· hero'}
                </span>
                <input
                  type="text"
                  value={sl.role}
                  onChange={(e) => updateSlide(sl.index, { role: e.target.value })}
                  className="flex-1 rounded border border-border/50 bg-background px-2 py-1 text-xs"
                  placeholder={sl.index === 0 ? 'hero' : 'cheese pull / crumb macro / cross-section…'}
                />
              </div>
              {sl.index === 0 ? (
                <textarea
                  rows={2}
                  value={sl.subjectHint}
                  onChange={(e) => updateSlide(sl.index, { subjectHint: e.target.value })}
                  className="w-full rounded-md border border-border/50 bg-background px-2.5 py-1.5 text-sm"
                  placeholder="the dish (ingredients + composition)"
                />
              ) : (
                <div className="space-y-1">
                  <div className="text-2xs text-muted-foreground">
                    Camera-only reframe of the hero — no new ingredients, no new plate, no new lighting.
                  </div>
                  <textarea
                    rows={2}
                    value={sl.framingDirective}
                    onChange={(e) => updateSlide(sl.index, { framingDirective: e.target.value })}
                    className="w-full rounded-md border border-border/50 bg-background px-2.5 py-1.5 text-sm"
                    placeholder='e.g. "Extreme macro top-down on the melted cheese pull, shallow depth of field."'
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between pt-2">
          <Button variant="outline" size="sm" onClick={() => configure({ step: 'setup' })}>
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Back
          </Button>
          <Button
            onClick={draftAllPrompts}
            disabled={
              state.busy
              || !state.slides[0]?.subjectHint.trim()
              || state.slides.slice(1).some((s) => !s.framingDirective.trim())
            }
          >
            {state.busy ? (
              <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Drafting {state.busySlide != null ? `slide ${state.busySlide + 1}` : 'prompts'}…</>
            ) : (
              <><Wand2 className="mr-1.5 h-3.5 w-3.5" /> Draft all prompts</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PromptsStep({ composer }: { composer: ReturnType<typeof useComposer> }) {
  const { state, updateSlide, configure, generateSlideImage, generateAllImages } = composer;
  const isSingle = state.postKind === 'single';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-base">
          <span>Step {isSingle ? 2 : 3} · {isSingle ? 'Prompt' : 'Prompts'}</span>
          {!isSingle && (
            <Button size="sm" onClick={generateAllImages} disabled={state.busy || state.slides.some((s) => !s.prompt)}>
              {state.busy ? (
                <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> {state.busySlide != null ? `Rendering slide ${state.busySlide + 1}…` : 'Rendering…'}</>
              ) : (
                <><Sparkles className="mr-1.5 h-3.5 w-3.5" /> Generate all slides</>
              )}
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {state.slides.map((sl) => (
          <SlidePromptCard
            key={sl.index}
            slide={sl}
            isSingle={isSingle}
            busy={state.busy && state.busySlide === sl.index}
            onSave={(prompt) => updateSlide(sl.index, { prompt })}
            onGenerate={(prompt) => generateSlideImage(sl.index, prompt)}
          />
        ))}
        <div className="flex justify-between pt-2">
          <Button variant="outline" size="sm" onClick={() => configure({ step: isSingle ? 'setup' : 'narrative' })}>
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Back
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SlidePromptCard({
  slide, isSingle, busy, onSave, onGenerate,
}: {
  slide: SlideState;
  isSingle: boolean;
  busy: boolean;
  onSave: (prompt: string) => void;
  onGenerate: (prompt: string) => void;
}) {
  const [edit, setEdit] = useState(slide.prompt ?? '');
  return (
    <div className="rounded-md border border-border/50 p-3">
      {!isSingle && (
        <div className="mb-1.5 flex items-center gap-2 text-xs">
          <span className="rounded bg-violet-500/10 px-1.5 py-0.5 text-2xs text-violet-500 tabular-nums">
            slide {slide.index + 1}
          </span>
          <span className="font-medium">{slide.role}</span>
          {slide.imageB64 && <span className="rounded bg-emerald-500/10 px-1.5 py-0.5 text-2xs text-emerald-500">rendered</span>}
        </div>
      )}
      {slide.refsUsed.length > 0 && (
        <div className="mb-1 flex flex-wrap gap-1">
          {slide.refsUsed.slice(0, 5).map((r) => (
            <span key={r} className="rounded bg-muted/50 px-1.5 py-0.5 text-2xs text-muted-foreground">
              {r.length > 30 ? r.slice(0, 30) + '…' : r}
            </span>
          ))}
        </div>
      )}
      <textarea
        rows={isSingle ? 10 : 5}
        value={edit}
        onChange={(e) => { setEdit(e.target.value); }}
        onBlur={() => onSave(edit)}
        className="w-full rounded-md border border-border/50 bg-background px-3 py-2 font-mono text-xs focus:border-violet-500 focus:outline-none"
      />
      <div className="mt-2 flex justify-end">
        <Button size="sm" onClick={() => onGenerate(edit)} disabled={busy || !edit.trim()}>
          {busy ? (
            <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Rendering…</>
          ) : (
            <><Sparkles className="mr-1.5 h-3.5 w-3.5" /> {slide.imageB64 ? 'Regenerate' : 'Generate'}</>
          )}
        </Button>
      </div>
    </div>
  );
}

function ImagesAndCaptionStep({ composer }: { composer: ReturnType<typeof useComposer> }) {
  const { state, updateSlide, generateSlideImage, draftCaption, updateCaption, scheduleDraft } = composer;
  const [captionHint, setCaptionHint] = useState('');
  const [captionEdit, setCaptionEdit] = useState(state.caption ?? '');
  const [hashtagsEdit, setHashtagsEdit] = useState((state.hashtags ?? []).join(' '));
  const [scheduleAt, setScheduleAt] = useState<string>(defaultScheduleAt());
  const [publishResult, setPublishResult] = useState<{ ok: boolean; media_id?: string; error?: string } | null>(null);
  const isSingle = state.postKind === 'single';

  if (state.caption && captionEdit === '' && state.step === 'caption') {
    setCaptionEdit(state.caption);
    setHashtagsEdit(state.hashtags.join(' '));
  }

  async function downloadZip() {
    const zip = new JSZip();
    state.slides.forEach((sl) => {
      if (!sl.imageB64) return;
      const bin = atob(sl.imageB64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const idx = String(sl.index + 1).padStart(2, '0');
      zip.file(`slide-${idx}.jpg`, bytes);
    });
    if (state.caption) zip.file('caption.txt', `${state.caption}\n\n${state.hashtags.join(' ')}`);
    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flax-${state.bucketSlug ?? 'post'}-${Date.now()}.zip`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function downloadSingle(sl: SlideState) {
    if (!sl.imageB64) return;
    const a = document.createElement('a');
    a.href = `data:${sl.imageMime};base64,${sl.imageB64}`;
    a.download = `flax-${state.bucketSlug ?? 'post'}-slide-${sl.index + 1}-${Date.now()}.jpg`;
    a.click();
  }

  const rendered = state.slides.filter((s) => s.imageB64).length;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">
            Step {isSingle ? 3 : 4} · {isSingle ? 'Image' : `Images (${rendered}/${state.slides.length})`}
          </CardTitle>
          {!isSingle && rendered === state.slides.length && (
            <Button size="sm" onClick={downloadZip}>
              <Package className="mr-1.5 h-3.5 w-3.5" /> Download ZIP
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className={cn(
            'grid gap-3',
            isSingle ? 'grid-cols-1' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
          )}>
            {state.slides.map((sl) => (
              <SlideImageCard
                key={sl.index}
                slide={sl}
                isSingle={isSingle}
                busy={state.busy && state.busySlide === sl.index}
                onRegenerate={() => generateSlideImage(sl.index)}
                onDownload={() => downloadSingle(sl)}
                onEditSubject={(subject) => updateSlide(sl.index, { subjectHint: subject })}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Step {isSingle ? 4 : 5} · Caption</CardTitle>
          {state.captionMs != null && (
            <span className="text-2xs text-muted-foreground tabular-nums">{(state.captionMs / 1000).toFixed(1)}s</span>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {!state.caption ? (
            <div className="space-y-2">
              <input
                type="text"
                value={captionHint}
                onChange={(e) => setCaptionHint(e.target.value)}
                placeholder="Optional hint (e.g. 'lead with sourdough fermentation')"
                className="w-full rounded-md border border-border/50 bg-background px-3 py-2 text-sm placeholder:text-muted-foreground/60"
              />
              <Button onClick={() => draftCaption(captionHint || undefined)} disabled={state.busy || !state.slides[0]?.imageB64}>
                {state.busy ? (
                  <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Claude Haiku is drafting…</>
                ) : (
                  <><Wand2 className="mr-1.5 h-3.5 w-3.5" /> Draft caption</>
                )}
              </Button>
              {!state.slides[0]?.imageB64 && (
                <div className="text-2xs text-muted-foreground">Generate slide 1 first.</div>
              )}
            </div>
          ) : (
            <>
              <div>
                <div className="mb-1 text-xs font-medium">Caption</div>
                <textarea
                  rows={5}
                  value={captionEdit}
                  onChange={(e) => setCaptionEdit(e.target.value)}
                  onBlur={() => updateCaption(captionEdit, hashtagsEdit.split(/\s+/).filter(Boolean))}
                  className="w-full rounded-md border border-border/50 bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <div className="mb-1 text-xs font-medium">Hashtags <span className="text-2xs text-muted-foreground/70">(space-separated)</span></div>
                <textarea
                  rows={2}
                  value={hashtagsEdit}
                  onChange={(e) => setHashtagsEdit(e.target.value)}
                  onBlur={() => updateCaption(captionEdit, hashtagsEdit.split(/\s+/).filter(Boolean))}
                  className="w-full rounded-md border border-border/50 bg-background px-3 py-2 font-mono text-2xs"
                />
              </div>
              <Button size="sm" variant="outline" onClick={() => draftCaption(captionHint || undefined)} disabled={state.busy}>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Redo caption
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {state.caption && state.slides.every((s) => s.imageB64) && (
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Publish · @flaxitup</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <BestSlotChips selected={scheduleAt} onPick={setScheduleAt} />
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-xs text-muted-foreground">Schedule for</label>
              <input
                type="datetime-local"
                value={scheduleAt}
                onChange={(e) => setScheduleAt(e.target.value)}
                className="rounded-md border border-border/50 bg-background px-2.5 py-1.5 text-sm"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  const iso = new Date(scheduleAt).toISOString();
                  const r = await scheduleDraft(iso);
                  setPublishResult(r);
                }}
                disabled={state.busy || !scheduleAt}
              >
                {state.busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Calendar className="mr-1.5 h-3.5 w-3.5" />}
                Schedule
              </Button>
              <div className="h-4 w-px bg-border/50" />
              <Button
                size="sm"
                onClick={async () => {
                  const r = await scheduleDraft(null);
                  setPublishResult(r);
                }}
                disabled={state.busy}
              >
                {state.busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
                Publish now
              </Button>
            </div>
            {publishResult && (
              <div className={cn(
                'rounded-md border px-3 py-2 text-xs',
                publishResult.ok ? 'border-emerald-500/40 bg-emerald-500/5 text-emerald-600 dark:text-emerald-400' : 'border-rose-500/40 bg-rose-500/5 text-rose-500',
              )}>
                {publishResult.ok ? (
                  <span className="flex items-center gap-1.5">
                    <Check className="h-3.5 w-3.5" />
                    Published · media id <code className="font-mono">{publishResult.media_id ?? '—'}</code>
                  </span>
                ) : (
                  <>Failed: {publishResult.error}</>
                )}
              </div>
            )}
            <p className="text-2xs text-muted-foreground">
              Scheduled posts fire automatically every 5 min (pg_cron worker). Publish now bypasses the queue and fires immediately.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function defaultScheduleAt(): string {
  // 1 hour from now, rounded to the nearest 5 min, in local timezone.
  const d = new Date(Date.now() + 60 * 60_000);
  d.setSeconds(0, 0);
  d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function SlideImageCard({
  slide, isSingle, busy, onRegenerate, onDownload,
}: {
  slide: SlideState;
  isSingle: boolean;
  busy: boolean;
  onRegenerate: () => void;
  onDownload: () => void;
  onEditSubject: (subject: string) => void;
}) {
  const imageSrc = slide.imageB64 ? `data:${slide.imageMime};base64,${slide.imageB64}` : null;
  return (
    <div className="space-y-1.5">
      {!isSingle && (
        <div className="flex items-center gap-1.5 text-2xs">
          <span className="rounded bg-violet-500/10 px-1.5 py-0.5 text-violet-500 tabular-nums">{slide.index + 1}</span>
          <span className="truncate text-muted-foreground">{slide.role}</span>
          {slide.imageMs != null && <span className="ml-auto text-muted-foreground/70 tabular-nums">{(slide.imageMs / 1000).toFixed(0)}s</span>}
        </div>
      )}
      <div className="relative aspect-square overflow-hidden rounded-md border border-border/40 bg-muted/20">
        {busy ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-violet-500" />
          </div>
        ) : imageSrc ? (
          <img src={imageSrc} alt={slide.role} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-2xs text-muted-foreground">
            <ImageIcon className="mr-1 h-3 w-3" /> not rendered yet
          </div>
        )}
      </div>
      {imageSrc && !busy && (
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="flex-1" onClick={onRegenerate}>
            <RefreshCw className="h-3 w-3" />
          </Button>
          <Button size="sm" className="flex-1" onClick={onDownload}>
            <Download className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
}

function KindChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-md border px-3 py-1.5 text-sm transition-colors',
        active ? 'border-violet-500 bg-violet-500/5 font-medium' : 'border-border/50 text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
}

function ReferencePicker({
  bucketSlug, subCategory, picked, onChange,
}: {
  bucketSlug: string;
  subCategory: string | null;
  picked: string[];
  onChange: (ids: string[]) => void;
}) {
  const refs = useContentReferences(bucketSlug, subCategory);
  const perf = useReferencePerformance();
  const qc = useQueryClient();
  const pickedSet = new Set(picked);
  const MAX = 5;

  const rescan = useMutation({
    mutationFn: async () => {
      const { data, error } = await marketing.functions.invoke('content-scan-trigger', {
        body: { bucket: bucketSlug },
      });
      if (error) throw new Error(error.message);
      return data as { ok?: boolean; per_bucket?: unknown };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content-references', bucketSlug] });
      qc.invalidateQueries({ queryKey: ['content-references-anchors', bucketSlug] });
    },
  });

  function toggle(id: string) {
    if (pickedSet.has(id)) onChange(picked.filter((x) => x !== id));
    else if (picked.length < MAX) onChange([...picked, id]);
  }

  // Sort refs: those with performance first, best-performing on top
  const rowsRaw = refs.data ?? [];
  const rows = [...rowsRaw].sort((a, b) => {
    const pa = perf.map.get(a.id)?.avg_engagement_pct ?? -1;
    const pb = perf.map.get(b.id)?.avg_engagement_pct ?? -1;
    if (pa === pb) return 0;
    return pb - pa;
  });
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <div className="text-sm font-medium">
          Reference photos
          <span className="ml-1.5 text-2xs text-muted-foreground">
            (Claude uses these to match style. Leave empty for auto-pick.)
          </span>
        </div>
        <div className="flex items-center gap-3 text-2xs">
          <button
            type="button"
            onClick={() => rescan.mutate()}
            disabled={rescan.isPending}
            className="flex items-center gap-1 text-muted-foreground underline hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
            title="Pull latest images from Drive"
          >
            {rescan.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            {rescan.isPending ? 'rescanning…' : rescan.isError ? 'rescan failed — retry' : 'rescan Drive'}
          </button>
          {picked.length > 0 && (
            <button type="button" onClick={() => onChange([])} className="text-muted-foreground underline hover:text-foreground">
              clear ({picked.length})
            </button>
          )}
        </div>
      </div>
      {refs.isLoading ? (
        <div className="text-xs text-muted-foreground">loading references…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-border/50 bg-muted/20 px-3 py-4 text-center text-xs text-muted-foreground">
          No references in this bucket yet. Drop images into the Drive folder and Rescan.
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
          {rows.map((r) => (
            <RefThumb key={r.id} ref_={r} active={pickedSet.has(r.id)} disabled={!pickedSet.has(r.id) && picked.length >= MAX} onClick={() => toggle(r.id)} perf={perf.map.get(r.id) ?? null} bestEng={perf.best} />
          ))}
        </div>
      )}
      <div className="mt-1.5 text-2xs text-muted-foreground">
        {picked.length === 0 ? 'auto-pick (Claude selects up to 5)' : `${picked.length}/${MAX} picked`}
      </div>
    </div>
  );
}

function CompositionAnchorPicker({
  bucketSlug, anchorId, carryVessel, onChange, onCarryVesselChange,
}: {
  bucketSlug: string;
  subCategory: string | null;
  pickedRefIds: string[];
  anchorId: string | null;
  carryVessel: boolean;
  onChange: (id: string | null) => void;
  onCarryVesselChange: (v: boolean) => void;
}) {
  // Reads the dedicated composition-anchor pool for this bucket — the
  // scanner tags images from `_composition` sub-folders and from
  // content_bucket.anchor_folder_id (e.g. the "aggregator images" folder).
  const anchors = useCompositionAnchors(bucketSlug);
  const [expanded, setExpanded] = useState(!!anchorId);
  const rows = anchors.data ?? [];
  const activeAnchor = rows.find((r) => r.id === anchorId) ?? null;

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <div className="text-sm font-medium">
          Composition anchor
          <span className="ml-1.5 rounded bg-muted/40 px-1.5 py-0.5 text-2xs font-normal text-muted-foreground">
            optional
          </span>
          <span className="ml-1.5 text-2xs text-muted-foreground">
            (copies ingredient arrangement only — style still comes from the refs above)
          </span>
        </div>
        <div className="flex items-center gap-2 text-2xs">
          {anchorId && (
            <button
              type="button"
              onClick={() => { onChange(null); setExpanded(false); }}
              className="text-muted-foreground underline hover:text-foreground"
            >
              clear
            </button>
          )}
          <button
            type="button"
            onClick={() => setExpanded((x) => !x)}
            className="text-muted-foreground hover:text-foreground"
          >
            {expanded ? 'hide' : 'pick one'}
          </button>
        </div>
      </div>
      {activeAnchor && !expanded && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 rounded-md border border-violet-500/30 bg-violet-500/5 p-2 text-xs">
            {(activeAnchor.drive_file_id || activeAnchor.drive_thumbnail_link) && (
              <img src={driveImgSrc(activeAnchor.drive_file_id, activeAnchor.drive_thumbnail_link, 96) ?? ''} alt="" referrerPolicy="no-referrer" className="h-10 w-10 rounded object-cover" />
            )}
            <div className="min-w-0 flex-1 truncate">{activeAnchor.original_name}</div>
            <span className="text-2xs text-violet-500">composition anchor</span>
          </div>
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border/40 bg-muted/10 px-2.5 py-1.5 text-xs">
            <input
              type="checkbox"
              checked={carryVessel}
              onChange={(e) => onCarryVesselChange(e.target.checked)}
              className="h-3.5 w-3.5 cursor-pointer accent-violet-500"
            />
            <span>
              Carry vessel too
              <span className="ml-1 text-2xs text-muted-foreground">
                (also copy the bowl/plate/container from anchor — style still comes from refs above)
              </span>
            </span>
          </label>
        </div>
      )}
      {expanded && (
        rows.length === 0 ? (
          <div className="rounded-md border border-dashed border-border/50 bg-muted/20 px-3 py-3 text-center text-xs text-muted-foreground">
            {anchors.isLoading ? 'loading anchors…' : 'No composition anchors yet. Add images to the bucket\'s anchor folder (e.g. /FOOD/_Composition/ or the external "aggregator images" folder) and Rescan Drive.'}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
            {rows.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => { onChange(r.id === anchorId ? null : r.id); setExpanded(false); }}
                className={cn(
                  'group relative aspect-square overflow-hidden rounded-md border transition-all',
                  anchorId === r.id ? 'border-violet-500 ring-2 ring-violet-500/40' : 'border-border/50 hover:border-border',
                )}
                title={r.original_name ?? r.id}
              >
                {(r.drive_file_id || r.drive_thumbnail_link) ? (
                  <img
                    src={driveImgSrc(r.drive_file_id, r.drive_thumbnail_link, 400) ?? ''}
                    alt={r.original_name ?? ''}
                    referrerPolicy="no-referrer"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = '0.2'; }}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-2xs text-muted-foreground">no thumb</div>
                )}
                {anchorId === r.id && (
                  <div className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-violet-500 text-2xs text-white">✓</div>
                )}
              </button>
            ))}
          </div>
        )
      )}
    </div>
  );
}

function RefThumb({ ref_, active, disabled, onClick, perf, bestEng }: { ref_: ContentReference; active: boolean; disabled: boolean; onClick: () => void; perf: RefPerf | null; bestEng: number }) {
  const eng = perf?.avg_engagement_pct ?? null;
  const isTop = eng != null && bestEng > 0 && eng >= bestEng * 0.85; // within 15% of the best
  const engStr = eng != null ? `${eng.toFixed(1)}%` : null;
  const tooltip = perf
    ? `${ref_.original_name ?? ref_.id} · used in ${perf.posts_used_in} post${perf.posts_used_in === 1 ? '' : 's'} · ${engStr ?? '—'} avg eng`
    : (ref_.original_name ?? ref_.id);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'group relative aspect-square overflow-hidden rounded-md border transition-all',
        active ? 'border-violet-500 ring-2 ring-violet-500/40'
          : isTop ? 'border-emerald-500/60 ring-1 ring-emerald-500/30'
          : 'border-border/50 hover:border-border',
        disabled && 'opacity-40',
      )}
      title={tooltip}
    >
      {(ref_.drive_file_id || ref_.drive_thumbnail_link) ? (
        <img
          src={driveImgSrc(ref_.drive_file_id, ref_.drive_thumbnail_link, 400) ?? ''}
          alt={ref_.original_name ?? ''}
          referrerPolicy="no-referrer"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = '0.2'; }}
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="flex h-full items-center justify-center text-2xs text-muted-foreground">no thumb</div>
      )}
      {perf && (
        <div className={cn(
          'absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 px-1 py-0.5 text-2xs font-medium tabular-nums text-white',
          isTop ? 'bg-emerald-500/80 backdrop-blur' : 'bg-black/60 backdrop-blur',
        )}>
          <span>{perf.posts_used_in}×</span>
          {engStr && <span>{engStr}</span>}
        </div>
      )}
      {active && (
        <div className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-violet-500 text-2xs text-white">✓</div>
      )}
    </button>
  );
}

function SubChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-2.5 py-1 text-2xs transition-colors',
        active ? 'border-violet-500 bg-violet-500/10 text-foreground' : 'border-border/50 text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
}
